import * as React from "react"
import { Link, useForm, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { CheckboxOption } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { RadioGroup, RadioOption } from "@/components/ui/radio"
import { Textarea } from "@/components/ui/textarea"
import { useBlockAutoReload } from "@/hooks/use-block-auto-reload"
import type { AccessApp, AccessGroupRow, AccessLevel, LevelOption } from "@/types/access"
import type { PageProps } from "@/types/inertia"

/**
 * A group's name, who it is for, whether new accounts join it, and its level on
 * every app. Shared by New and Edit.
 */
export function AccessGroupForm({
  group,
  apps,
  levels,
  submitLabel,
}: {
  group: AccessGroupRow
  apps: AccessApp[]
  levels: LevelOption[]
  submitLabel: string
}) {
  const errors = usePage<PageProps>().props.errors ?? {}

  const { data, setData, transform, post, patch, processing, isDirty } = useForm({
    name: group.name,
    description: group.description ?? "",
    joined_at_first_sign_in: group.joined_at_first_sign_in,
    levels: group.levels,
  })

  useBlockAutoReload(isDirty)

  const setLevel = (key: string, level: AccessLevel) =>
    setData("levels", { ...data.levels, [key]: level })

  const submit = (event: React.FormEvent) => {
    event.preventDefault()

    // Nested explicitly: ParamsWrapper keeps only real columns under
    // `access_group`, and levels is not one.
    transform((form) => ({ access_group: form }))

    if (group.id) {
      patch(`/admin/access_groups/${group.id}`, { preserveScroll: true })
    } else {
      post("/admin/access_groups")
    }
  }

  return (
    <form onSubmit={submit} className="mt-8 space-y-8">
      <div className="space-y-4">
        <div className="max-w-md space-y-2">
          <label htmlFor="name">Name</label>
          <Input
            id="name"
            value={data.name}
            maxLength={60}
            onChange={(event) => setData("name", event.target.value)}
            aria-invalid={Boolean(errors.name)}
            placeholder="DC Staff"
            autoComplete="off"
          />
          {errors.name && <p className="text-xs text-danger-display">{errors.name}</p>}
        </div>

        <div className="space-y-2">
          <label htmlFor="description">Who it is for</label>
          <Textarea
            id="description"
            rows={2}
            maxLength={500}
            value={data.description}
            onChange={(event) => setData("description", event.target.value)}
            aria-invalid={Boolean(errors.description)}
          />
          {errors.description ? (
            <p className="text-xs text-danger-display">{errors.description}</p>
          ) : (
            <p className="text-xs text-ink-muted">
              A sentence, so the next admin knows who belongs in it.
            </p>
          )}
        </div>

        <CheckboxOption
          checked={data.joined_at_first_sign_in}
          onChange={(event) => setData("joined_at_first_sign_in", event.target.checked)}
          label="Put brand-new accounts in this group"
          description="Applied once, at a person's first Duo sign-in. Removing them later sticks."
        />
      </div>

      <fieldset className="space-y-3">
        <legend>Level on each app</legend>
        <p className="text-xs text-ink-muted">
          Someone in several groups gets the highest level any of them gives. Admins
          always have Edit, and an override on a person&rsquo;s own page beats every
          group.
        </p>

        <ul className="divide-y divide-hairline rounded-md border border-hairline bg-page">
          {apps.map((app) => (
            <li
              key={app.key}
              className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-ink-display">{app.label}</div>
                <div className="text-xs text-ink-muted">
                  {app.iupat_only
                    ? "IUPAT staff only — District Council users have No Access whatever this says."
                    : app.description}
                </div>
              </div>

              <RadioGroup
                orientation="horizontal"
                aria-label={`Level on ${app.label}`}
                className="shrink-0"
              >
                {levels.map((level) => (
                  <RadioOption
                    key={level.value}
                    name={`level_${app.key}`}
                    value={level.value}
                    label={level.label}
                    checked={(data.levels[app.key] ?? "no_access") === level.value}
                    onChange={() => setLevel(app.key, level.value)}
                  />
                ))}
              </RadioGroup>
            </li>
          ))}
        </ul>
      </fieldset>

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={processing}>
          {processing ? "Saving…" : submitLabel}
        </Button>
        <Button variant="ghost" asChild>
          <Link href="/admin/access_groups">Cancel</Link>
        </Button>
      </div>
    </form>
  )
}
